<script setup lang="ts">
import { onMounted } from 'vue';
import { computedAsync } from '@vueuse/core';

import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { performanceMark } from '@libs/utils';
import { isExperimentAsync } from '@libs/feature-toggle';

import {
  ItinerarySegmentType,
  isItineraryLocationSegment,
  isItineraryDateLocationSegment,
  isItinerarySimpleTransportSegment,
} from '../../models';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';
import { GlobalSuggestionTypeSwitch } from '../globalSuggestionType';

import ItineraryTimeline from './ItineraryTimeline.vue';
import ItineraryNoTransportSegment from './ItineraryNoTransportSegment.vue';
import ItineraryUpsellNoTransportSegment from './ItineraryUpsellNoTransportSegment.vue';
import ItinerarySimpleTransportSegment from './ItinerarySimpleTransportSegment.vue';
import ItineraryAccommodationSegment from './accommodation/ItineraryAccommodationSegment.vue';
import ItineraryActivitySegment from './acitivity/ItineraryActivitySegment.vue';
import ItineraryFerrySegment from './ferry/ItineraryFerrySegment.vue';
import ItineraryFlightSegment from './flight/ItineraryFlightSegment.vue';
import ItineraryLocationSegment from './location/ItineraryLocationSegment.vue';
import ItineraryDateLocationSegment from './location/ItineraryDateLocationSegment.vue';
import ItineraryUpsellSegment from './upsell/ItineraryUpsellSegment.vue';
import ItineraryOwnStaySegment from './ownStay/ItineraryOwnStaySegment.vue';
import AccommodationUpsellSegment from './upsell/accommodation/AccommodationUpsellSegment.vue';
import AddDestinationPlaceholder from './AddDestinationPlaceholder.vue';

interface TripItineraryProps {
  printMode: boolean;
}

const props = defineProps<TripItineraryProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const browserStore = useBrowserStore();

const overviewPlaceholderNoTransportExperiment = computedAsync<boolean>(
  () => isExperimentAsync('overview_placeholder_no_transport_experiment'),
  false
);

const shouldHideProductLink = computedAsync<boolean>(
  () => isExperimentAsync('remove_product_links'),
  false
);

const isTopAddDestinationOverviewButtonExperiment = computedAsync<boolean>(
  () => isExperimentAsync('top_add_destination_overview_experiment'),
  false
);

onMounted(() => {
  if (tripOverviewPageStore.isFirstTripItineraryRender) {
    performanceMark('FirstItineraryRendered');
    tripOverviewPageStore.isFirstTripItineraryRender = false;
  }
});

function openPriceCalendar(): void {
  if (browserStore.isSmallerThan900px) {
    tripOverviewPanelsStore.openConfigurationPanel(
      TabKey.Calendar,
      'low-price-calendar'
    );
  } else {
    tripOverviewModalsStore.openTripCalendarModal();
  }
}

function openEditTripModal(): void {
  if (browserStore.isSmallerThan900px) {
    tripOverviewPanelsStore.openConfigurationPanel(
      TabKey.Itinerary,
      'date-location-segment'
    );
  } else {
    tripOverviewModalsStore.openEditTripModal();
  }
}
</script>

<template>
  <div class="trip-itinerary">
    <div v-if="!props.printMode" class="global-suggestion">
      <GlobalSuggestionTypeSwitch
        v-if="browserStore.isSmallerThan1200px"
        @low-price-calendar-click="openPriceCalendar"
      />
    </div>

    <div
      v-for="(segment, index) in tripOverviewPageStore.tripItinerary"
      :key="index"
      :class="{
        'trip-itinerary-first-segment': index === 0,
      }"
    >
      <ItineraryTimeline
        v-if="
          !props.printMode &&
          isTopAddDestinationOverviewButtonExperiment &&
          index === 0
        "
        :segment-type="ItinerarySegmentType.StartLocation"
      >
        <template #banner>
          <AddDestinationPlaceholder origin="topTest" class="test" />
        </template>
      </ItineraryTimeline>
      <ItineraryDateLocationSegment
        v-if="isItineraryDateLocationSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
        @segment-clicked="openEditTripModal"
      />
      <ItineraryLocationSegment
        v-if="isItineraryLocationSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.NoTransport &&
          !props.printMode &&
          (!overviewPlaceholderNoTransportExperiment ||
            (overviewPlaceholderNoTransportExperiment &&
              tripOverviewPageStore.hasUserSelectedTransport))
        "
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.UpsellNoTransport &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItinerarySimpleTransportSegment
        v-if="isItinerarySimpleTransportSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
        :should-hide-product-link="shouldHideProductLink"
      />

      <ItineraryAccommodationSegment
        v-if="segment.segmentType === ItinerarySegmentType.Accommodation"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryOwnStaySegment
        v-if="
          segment.segmentType === ItinerarySegmentType.OwnStay &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItineraryActivitySegment
        v-if="segment.segmentType === ItinerarySegmentType.Activity"
        :segment="segment"
        :print-mode="props.printMode"
        :should-hide-product-link="shouldHideProductLink"
      />

      <ItineraryFerrySegment
        v-if="segment.segmentType === ItinerarySegmentType.Ferry"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryFlightSegment
        v-if="segment.segmentType === ItinerarySegmentType.Flight"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellSegment
        v-else-if="
          segment.segmentType === ItinerarySegmentType.Upsell &&
          !props.printMode
        "
        :data="segment.productCardData"
        :segment-type="segment.segmentType"
      />

      <AccommodationUpsellSegment
        v-else-if="
          segment.segmentType === ItinerarySegmentType.AccommodationUpsell &&
          !props.printMode
        "
        :segment="segment"
      />
    </div>

    <AddDestinationPlaceholder v-if="!props.printMode" origin="bottom" />
  </div>
</template>

<style scoped lang="scss">
.trip-itinerary {
  .global-suggestion {
    box-shadow: var(--shadow-elevation-200);
    position: relative;

    position: sticky;
    top: 0;
    z-index: var(--modal-overlay-z-index);
  }
  .test {
    margin: 0;
    @include for-less-or-equal-than-1200-px {
      margin-left: var(--spacing-8);
    }
    @include for-less-or-equal-than-600-px {
      margin-left: var(--spacing-0);
    }
  }
  .trip-itinerary-first-segment {
    @include for-less-or-equal-than-1199-px {
      margin-top: var(--spacing-24);
    }
  }
}
</style>
